/**
 * AdminTaskStatusSelector — Pill group de 3 statuts admin (Batch #14.211).
 *
 * Props :
 *   - itemType : clé ITEM_TYPES (work_order, rent_increase, rl31_request, etc.)
 *   - itemId : ID du document
 *   - value : statut courant ("en_attente" | "en_cours" | "termine" | undefined)
 *   - onChanged : callback après changement réussi
 *   - size : "xs" | "sm" (default xs)
 *
 * Visuel : 3 petites pills colorées. Cliquer change le statut via PATCH.
 */
import { useState } from "react";
import { Clock, Loader2, CheckCircle2, PlayCircle } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";

const STATUSES = [
  { key: "en_attente", label: "En attente", Icon: Clock,         activeCls: "bg-slate-200 text-slate-800 border-slate-400" },
  { key: "en_cours",   label: "En cours",   Icon: PlayCircle,    activeCls: "bg-amber-100 text-amber-800 border-amber-400" },
  { key: "termine",    label: "Terminé",    Icon: CheckCircle2,  activeCls: "bg-emerald-100 text-emerald-800 border-emerald-400" },
];

export default function AdminTaskStatusSelector({ itemType, itemId, value, onChanged, size = "xs" }) {
  const [current, setCurrent] = useState(value || "en_attente");
  const [busy, setBusy] = useState(null);

  const handleSet = async (newStatus) => {
    if (newStatus === current || busy) return;
    setBusy(newStatus);
    try {
      await api.patch(`/admin/task-status/${itemType}/${itemId}`, { admin_task_status: newStatus });
      setCurrent(newStatus);
      toast.success(`Statut → ${STATUSES.find((s) => s.key === newStatus)?.label}`);
      onChanged?.(newStatus);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur statut");
    } finally {
      setBusy(null);
    }
  };

  const isXs = size === "xs";
  const padding = isXs ? "px-1.5 py-0.5" : "px-2 py-1";
  const iconSize = isXs ? "w-2.5 h-2.5" : "w-3 h-3";
  const textSize = isXs ? "text-[9px]" : "text-[10px]";

  return (
    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-white" data-testid={`task-status-${itemId}`}>
      {STATUSES.map((s) => {
        const Icon = s.Icon;
        const isActive = current === s.key;
        const isBusy = busy === s.key;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => handleSet(s.key)}
            disabled={isBusy || isActive}
            className={`inline-flex items-center gap-0.5 ${padding} ${textSize} font-medium border-l first:border-l-0 transition ${
              isActive ? `${s.activeCls} font-bold` : "border-slate-200 text-slate-500 hover:bg-slate-50"
            } disabled:cursor-default`}
            title={isActive ? `Statut: ${s.label}` : `Marquer comme ${s.label.toLowerCase()}`}
            data-testid={`task-status-${itemId}-${s.key}`}
          >
            {isBusy ? <Loader2 className={`${iconSize} animate-spin`} /> : <Icon className={iconSize} />}
            <span>{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}
